/* eslint-disable */
import moment from 'moment';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import { notificationServices } from '@/Services';
import { modalActions } from '@/Actions';
import { CLOSED_CONFIRMATION } from './createOrdersUiHelpers';
import { flowDate } from './createOrdersApiConfig';
import { runValidators } from './createOrdersValidators';
import {  getUnitInLowerUnit } from '@/Helpers';

export const collectOrderIds = (tables = {}) => [...new Set(
	Object.values(tables || {}).flatMap((table) => table?.orderIds || [])
)].filter((id) => id != null);

const isDateInPast = (state) => {
	const date = flowDate(state);
	if (!date) return false;
	return moment(date).startOf('day').isBefore(moment().startOf('day'));
};

const planDate = (state) => moment(flowDate(state)).format('YYYY-MM-DD');

const META_COLUMN_IDS = ['S.No', 'Item Name', 'SkuCode', 'Total', 'Actions'];

const changedColumns = (tableData) => {
	const columns = (tableData?.headers || []).filter(
		(h) => !META_COLUMN_IDS.includes(h?.id)
	);
	return columns.filter((col) => (tableData?.rows || []).some((row) => {
		const cell = row?.[col.id];
		if (!cell) return false;
		const qtyFilled = cell?.qty !== '' && cell?.qty != null && Number(cell.qty) > 0;
		return cell?._edited || (qtyFilled && !cell?.sources?.length);
	}));
};

const hasOrderChanges = (tableData) => changedColumns(tableData).length > 0;

const withUpdatePrefix = ({ step, tableData, label }) => (
	step?.onUpdate && hasOrderChanges(tableData) ? `Update Orders and ${label}` : label
);


const buildCancelRecreatePayload = ({ step, state, tableData }) => {
	const cols = changedColumns(tableData);
	if (!cols.length) return null;
	const isBuyer = step?.tableKey === 'buyerOrders';
	const cancelPoIds = new Set();
	const orderInfo = [];
	cols.forEach((col) => {
		const items = [];
		(tableData?.rows || []).forEach((row) => {
			const cell = row?.[col.id];
			if (!cell) return;
			(cell?.sources || []).forEach((source) => {
				if (source?.poId != null) cancelPoIds.add(source.poId);
			});
			const qty = Number(cell?.qty);
			if (!qty || qty <= 0) return;
			items.push({
				variantId: cell?.itemId,
				productId: cell?.productId,
				qty: getUnitInLowerUnit(cell?.unit ?? cell?.baseUnit, qty),
			});
		});
		if (!items.length) return;
		if (isBuyer) {
			const [vendorId, locationId] = String(col?.id).split('_');
			orderInfo.push({
				vendorId: Number(vendorId),
				vendorlocationId: Number(locationId),
				items,
			});
		} else {
			orderInfo.push({
				store: Number(col?.id),
				items,
				priority: false,
			});
		}
	});
	if (!orderInfo.length) return null;
	const base = {
		date: moment(new Date()).format('MM/DD/YYYY'),
		scheduled: moment(new Date(flowDate(state))).format('MM/DD/YYYY'),
		transferOrderType: 'DISPATCH',
		workOrderType: 'PRODUCTION PLAN',
		fromStore: state?.fromStore,
		orderInfo,
		cancelPoIds: [...cancelPoIds],
	};
	return isBuyer
		? {
			...base,
			orderType: 8,
			type: 'BUYER',
			store: state?.fromStore,
		}
		: {
			...base,
			orderType: 2,
			type: 'PURCHASEORDER',
			appOrderType: 'PO',
		};
};

const NO_SAVE = { ok: true, recreated: false, cancelledPoIds: [], newOrderIds: [] };

const saveEdits = async ({ step, state, tableData, setTableData, changeQtyRef }) => {
	if (!step?.onUpdate || !hasOrderChanges(tableData)) return NO_SAVE;
	const payload = buildCancelRecreatePayload({ step, state, tableData });
	if (!payload) return NO_SAVE;
	const count = payload.orderInfo.length;
	const notificationId = notificationServices.generateNotification({
		type: 'loading',
		message: `Updating ${count} order${count > 1 ? 's' : ''}…`,
	});
	try {
		const response = await inventoryAnalyticsServices.addVendorPO({
			data: payload,
		});
		if (response?.statusCode !== 200) {
			notificationServices.generateNotification({
				type: 'error',
				message: response?.errorMessage
					?? response?.message
					?? 'Could not update the orders',
				config: { id: notificationId },
			});
			return { ...NO_SAVE, ok: false };
		}
		notificationServices.generateNotification({
			type: 'success',
			message: response?.message
				?? `Cancelled ${payload.cancelPoIds.length} and created ${count} order${count > 1 ? 's' : ''}`,
			config: { id: notificationId },
		});
		setTableData?.((prev) => ({
			...prev,
			rows: (prev?.rows || []).map((row) => {
				const next = { ...row };
				Object.keys(next).forEach((key) => {
					if (next[key]?._edited) {
						next[key] = { ...next[key], _edited: false };
					}
				});
				return next;
			}),
		}));
		if (changeQtyRef) changeQtyRef.current = false;
		const newOrderIds = (response?.data?.orders || [])
			.map((order) => order?.id ?? order)
			.filter((id) => id != null);
		return {
			ok: true,
			recreated: true,
			cancelledPoIds: payload.cancelPoIds,
			newOrderIds,
		};
	} catch (error) {
		console.error('Error re-creating orders:', error);
		notificationServices.generateNotification({
			type: 'error',
			message: error ?? 'Could not update the orders',
			config: { id: notificationId },
		});
		return { ...NO_SAVE, ok: false };
	}
};

const openProductionPlan = async ({ state, dispatch }) => {
	dispatch?.(
		modalActions?.toggleModal('initiateProductionPlanModal', true, {
			kitchenId: state?.fromStore,
			date: planDate(state),
			planName: state?.planName,
		})
	);
};

export const createOrdersButtonConfig = {
	nextStep: {
		direction: 1,
		isVisible: ({ isLastStep, step }) => !isLastStep || step?.type === 'form',
		getLabel: withUpdatePrefix,
		getDisabled: ({ isPrefetching }) => (
			isPrefetching ? 'Loading orders…' : false
		),
		onClick: async (ctx) => {
			const result = await saveEdits(ctx);
			if (!result.ok) return;
			const tables = await ctx.prefetchTables?.(result.recreated);
			if (tables === null) return;
			const loaded = tables ?? ctx.tables;
			if (!collectOrderIds(loaded).length) {
				const close = () => ctx.setConfirmationModal((prev) => ({
					...prev,
					...CLOSED_CONFIRMATION,
				}));
				ctx.setConfirmationModal((prev) => ({
					...prev,
					...CLOSED_CONFIRMATION,
					isOpen: true,
					isAlert: true,
					alertType: 'warning',
					heading: 'No Orders Found',
					message: 'No pending buyer or internal sales orders were found for the selected kitchen and dates. Create the orders first, or pick a different kitchen, and try again.',
					handleCloseConfirmation: close,
					handleConfirmSave: close,
				}));
				return;
			}
			ctx.goToStep?.(1);
		},
	},
	previousStep: {
		direction: -1,
		onClick: ({ goToStep }) => goToStep?.(-1),
	},
	updateOrder: {
		isVisible: ({ step, tableData }) => (
			Boolean(step?.onUpdate) && hasOrderChanges(tableData)
		),
		onClick: async (ctx) => {
			const result = await saveEdits(ctx);
			if (result.ok && result.recreated) await ctx.prefetchTables?.(true);
		},
	},
	generateProductionPlan: {
		isVisible: ({ isLastStep }) => Boolean(isLastStep),
		getLabel: withUpdatePrefix,
		getDisabled: ({ state, tables, isPrefetching }) => {
			if (isPrefetching) return 'Loading orders…';
			if (!state?.fromStore) {
				return 'Select a kitchen before initiating a production plan';
			}
			if (!collectOrderIds(tables).length) {
				return 'No orders found to initiate a production plan';
			}
			if (isDateInPast(state)) {
				return "Can't initiate a production plan: the selected date is before today";
			}
			return false;
		},
		onClick: (ctx) => {
			const { setConfirmationModal, tables, tableData } = ctx;
			const count = collectOrderIds(tables).length;
			const saving = hasOrderChanges(tableData);
			const close = () => setConfirmationModal((prev) => ({
				...prev,
				...CLOSED_CONFIRMATION,
			}));
			setConfirmationModal((prev) => ({
				...prev,
				...CLOSED_CONFIRMATION,
				isOpen: true,
				heading: 'Initiate Production Plan',
				message: (saving ? 'Your quantity changes will be saved first. ' : '')
					+ `A production plan will be initiated for ${count} order${count > 1 ? 's' : ''}. Continue?`,
				handleCloseConfirmation: close,
				handleConfirmSave: async () => {
					setConfirmationModal((prev) => ({ ...prev, isLoading: true }));
					try {
						// Save before planning — the plan is built from what the
						// server has, so unsaved edits would be left out.
						const result = await saveEdits(ctx);
						if (!result.ok) return;
						await openProductionPlan(ctx);
					} finally {
						close();
					}
				},
			}));
		},
	},
};

export const getButtonConfig = (buttonId) => (
	createOrdersButtonConfig[buttonId] || {}
);

export const getDisabledReason = (buttonId, ctx) => (
	getButtonConfig(buttonId)?.getDisabled?.(ctx) || null
);

export const isButtonVisible = (buttonId, ctx) => {
	const check = getButtonConfig(buttonId)?.isVisible;
	return check ? Boolean(check(ctx)) : true;
};

export const getButtonLabel = (btn, ctx, fallbackLabel) => {
	const build = getButtonConfig(btn?.id)?.getLabel;
	return build ? build({ ...ctx, label: fallbackLabel }) : fallbackLabel;
};

export const handleButtonClick = (btn, ctx) => {
	const result = runValidators(btn?.validators || [], ctx);
	if (!result.valid) {
		notificationServices.generateNotification({
			type: 'error',
			message: result.message || 'Validation failed',
		});
		return;
	}
	getButtonConfig(btn?.id)?.onClick?.(ctx);
};

export default createOrdersButtonConfig;
